// Custom Element for Waitlist Modal with Klaviyo Integration
class WaitlistModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.overlay = null;
    this.closeBtn = null;
    this.form = null;
    this.emailInput = null;
    this.errorMessage = null;
    this.successMessage = null;
    this.submitButton = null;
    this.buttonText = null;
    this.buttonLoading = null;
    this.productInfoElement = null;
    this.variantsData = null;
  }

  connectedCallback() {
    this.initializeElements();
    this.loadVariantsData();
    this.attachEventListeners();
  }

  initializeElements() {
    this.modal = this.querySelector('#waitlist-modal');
    this.overlay = this.querySelector('.waitlist-modal-overlay');
    this.closeBtn = this.querySelector('.waitlist-modal-close');
    this.form = this.querySelector('#waitlist-form');
    this.emailInput = this.querySelector('#waitlist-email');
    this.errorMessage = this.querySelector('.waitlist-error-message');
    this.successMessage = this.querySelector('.waitlist-success-message');
    this.submitButton = this.querySelector('.waitlist-submit-button');
    this.buttonText = this.querySelector('.waitlist-button-text');
    this.buttonLoading = this.querySelector('.waitlist-button-loading');
    this.productInfoElement = document.getElementById('waitlist-product-info');
  }

  loadVariantsData() {
    const variantsScript = document.getElementById('waitlist-variants-data');
    if (variantsScript) {
      try {
        this.variantsData = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Error parsing variants data:', e);
      }
    }
  }

  attachEventListeners() {
    // Open modal when waitlist button is clicked
    document.addEventListener('click', (e) => {
      const waitlistButton = e.target.closest('[data-waitlist-trigger]');
      if (waitlistButton) {
        e.preventDefault();
        e.stopPropagation();
        this.updateVariantBeforeOpen();
        this.openModal();
      }
    });

    // Close modal on overlay or close button click
    this.overlay?.addEventListener('click', () => this.closeModal());
    this.closeBtn?.addEventListener('click', () => this.closeModal());

    // Close modal on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
        this.closeModal();
      }
    });

    // Handle form submission
    this.form?.addEventListener('submit', (e) => {
      this.handleFormSubmit(e);
    });

    // Listen for variant changes from variant picker (radio buttons)
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (target.matches('variant-picker input[type="radio"]') ||
          target.matches('[data-variant-id]') ||
          target.matches('[name="id"]') ||
          target.matches('select[name="id"]')) {
        this.handleVariantChange(target); 
      }
    });

    // Listen for custom variant:changed event
    document.addEventListener('variant:changed', (e) => {
      if (e.detail?.variant) {
        this.updateVariantData(e.detail.variant.id, e.detail.variant.title);
      }
    });
  }

  handleVariantChange(target) {
    // Get variant ID from different sources
    let variantId = target.dataset?.variantId || target.value;

    if (variantId && this.variantsData) {
      const variant = this.variantsData.variants.find(v => v.id == variantId);
      if (variant) {
        this.updateVariantData(variant.id, variant.title);
      }
    }
  }

  updateVariantBeforeOpen() {
    // Try to get current selected variant from the page
    // Check radio buttons first (variant picker)
    const checkedRadio = document.querySelector('variant-picker input[type="radio"]:checked[data-variant-id]');
    if (checkedRadio) {
      this.handleVariantChange(checkedRadio);
      return;
    }

    // Check select element
    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect) {
      this.handleVariantChange(variantSelect);
      return;
    }

    // Check hidden input
    const hiddenInput = document.querySelector('input[name="id"]');
    if (hiddenInput) {
      this.handleVariantChange(hiddenInput);
      return;
    }

    // Try to build variant title from selected options
    this.buildVariantFromSelectedOptions();
  }

  buildVariantFromSelectedOptions() {
    // Get all checked radio buttons in variant picker
    const checkedOptions = document.querySelectorAll('variant-picker input[type="radio"]:checked');
    if (checkedOptions.length > 0) {
      const optionValues = [];
      checkedOptions.forEach(radio => {
        optionValues.push(radio.value);
      });

      const variantTitle = optionValues.join(' / ');

      // Find matching variant
      if (this.variantsData) {
        const variant = this.variantsData.variants.find(v => v.title === variantTitle);
        if (variant) {
          this.updateVariantData(variant.id, variant.title);
          return;
        }
      }

      // If no exact match, just update the display
      this.updateProductInfoDisplay(variantTitle);
    }
  }

  updateVariantData(variantId, variantTitle) {
    if (this.modal) {
      this.modal.dataset.variantId = variantId;
      this.modal.dataset.variantTitle = variantTitle || '';
    }
    this.updateProductInfoDisplay(variantTitle);
  }

  updateProductInfoDisplay(variantTitle) {
    if (this.productInfoElement && this.modal) {
      const productTitle = this.modal.dataset.productTitle;
      let displayText = productTitle;

      if (variantTitle && variantTitle !== 'Default Title') {
        displayText = `${productTitle}, ${variantTitle}`;
      }

      this.productInfoElement.textContent = displayText;
    }
  }

  openModal() {
    this.modal?.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.emailInput?.focus();
    
  }

  closeModal() {
    this.modal?.classList.remove('active');
    document.body.style.overflow = '';
    this.resetForm();
  }

  resetForm() {
    this.form?.reset();
    this.emailInput?.classList.remove('error');
    if (this.errorMessage) this.errorMessage.style.display = 'none';
    if (this.successMessage) this.successMessage.style.display = 'none';
    if (this.form) this.form.style.display = 'block';
    if (this.submitButton) this.submitButton.disabled = false;
    if (this.buttonText) this.buttonText.style.display = 'inline';
    if (this.buttonLoading) this.buttonLoading.style.display = 'none';
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    const email = this.emailInput?.value.trim();
    const variantId = this.modal?.dataset.variantId;
    const productTitle = this.modal?.dataset.productTitle;
    const variantTitle = this.modal?.dataset.variantTitle;

    // Validate email
    if (!this.validateEmail(email)) {
      this.emailInput?.classList.add('error');
      if (this.errorMessage) {
        this.errorMessage.textContent = 'Please enter a valid email address';
        this.errorMessage.style.display = 'block';
      }
      return;
    }

    // Clear errors
    this.emailInput?.classList.remove('error');
    if (this.errorMessage) this.errorMessage.style.display = 'none';

    // Show loading state
    if (this.submitButton) this.submitButton.disabled = true;
    if (this.buttonText) this.buttonText.style.display = 'none';
    if (this.buttonLoading) this.buttonLoading.style.display = 'inline';

    try {
      // Subscribe to Klaviyo Back in Stock and track event
      await this.subscribeToKlaviyo(email, variantId, productTitle, variantTitle);

      // Show success message
      if (this.form) this.form.style.display = 'none';
      if (this.successMessage) this.successMessage.style.display = 'block';

      // Close modal after 3 seconds
      setTimeout(() => this.closeModal(), 3000);

    } catch (error) {
      console.error('Error submitting waitlist form:', error);

      // Show error message
      if (this.errorMessage) {
        this.errorMessage.textContent = 'Something went wrong. Please try again.';
        this.errorMessage.style.display = 'block';
      }

      // Reset button state
      if (this.submitButton) this.submitButton.disabled = false;
      if (this.buttonText) this.buttonText.style.display = 'inline';
      if (this.buttonLoading) this.buttonLoading.style.display = 'none';
    }
  }

  async subscribeToKlaviyo(email, variantId, productTitle, variantTitle) {

    const KLAVIYO_COMPANY_ID = 'UF2YUH';
    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${KLAVIYO_COMPANY_ID}`;

    // Formato del variant ID para Shopify: $shopify:::$default:::VARIANT_ID
    const catalogVariantId = `$shopify:::$default:::${variantId}`;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
              }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: catalogVariantId
            }
          }
        }
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 202) {
        return { success: true };
      }

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.errors?.[0]?.detail || 'Subscription failed');
      }

      return { success: true, data: responseData };

    } catch (error) {
      console.error('[BIS] ERROR:', error);
      throw error;
    }
  }

  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  disconnectedCallback() {
    document.body.style.overflow = '';
  }
}

// Register the custom element
customElements.define('waitlist-modal', WaitlistModal);